"use client";

import { IconX } from "@tabler/icons-react";
import clsx from "clsx";
import { type default as React, useCallback, useEffect, useRef, useState } from "react";
import type { ModerationProfile } from "shared/ModerationProfile";
import type { OrganisationSummary } from "shared/Organisation";
import type { Page } from "shared/Page";
import type { Project } from "shared/Project";
import type { ProjectTeam } from "shared/ProjectAccess";
import { FIRST_PARTY_COOKIES } from "shared/SharedConstants";
import type { AuthState } from "shared/UtilTypes";
import { StatusPageLayout } from "src/components/app/StatusPage/StatusPageLayout";
import IconButton from "src/components/common/IconButton/IconButton";
import SplitPane, { SplitPanePanel } from "src/components/common/SplitPane/SplitPane";
import DocsLoadedNotifier from "src/components/docs/DocsLoadedNotifier";
import { AuthProvider } from "src/lib/contexts/AuthProvider";
import { PortalProvider } from "src/lib/contexts/PortalProvider";
import { usePathname, useSearchParams } from "src/lib/Navigation";
import type { PortalShellData } from "src/lib/PortalShellData";
import { useExtracted } from "#i18n";
import styles from "./AppLayout.module.scss";
import PortalTopbar from "./PortalTopbar";

export interface AppLayoutProps {
  initialShellData?: PortalShellData;
  children: React.ReactNode;
  initialProjects: Page<Project>;
  initialOrganisations?: OrganisationSummary[];
  initialSelectedOrgId?: string | null;
  initialAuthState: AuthState | undefined;
  initialSelectedProjectId: string | null;
  initialProjectTeam?: ProjectTeam;
  initialModerationProfiles?: ModerationProfile[];
  /** The navigation sidebar (portal or docs), also shown in the mobile navigation dialog. */
  sidebar: React.ReactNode;
  /** Renders the sidebar, and the mobile navigation dialog that shows it, in a fixed theme. */
  sidebarTheme?: "dark";
  /** Shown at the end of the content column, beside the sidebar, after the page. */
  footer?: React.ReactNode;
}

const AppLayout = (props: AppLayoutProps) => {
  const translateMessage = useExtracted();
  const {
    children,
    initialProjects,
    initialOrganisations,
    initialSelectedOrgId,
    initialAuthState,
    initialSelectedProjectId,
    initialProjectTeam,
    initialModerationProfiles,
    sidebar,
    sidebarTheme,
    footer,
  } = props;

  let embedded = false;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  if (searchParams.get("embedded") === "true") {
    embedded = true;
  }
  const [embeddedNavigationOpen, setEmbeddedNavigationOpen] = useState(false);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const navigationPanelRef = useRef<HTMLDivElement>(null);
  const navigationOpen = embedded ? embeddedNavigationOpen : mobileNavigationOpen;

  const closeEmbeddedNavigation = useCallback(() => {
    setEmbeddedNavigationOpen(false);
    window.parent.postMessage(
      { type: "embeddedDocsNavigationChanged", open: false },
      window.location.origin,
    );
  }, []);

  const closeNavigation = useCallback(() => {
    if (embedded) {
      closeEmbeddedNavigation();
      return;
    }
    setMobileNavigationOpen(false);
  }, [closeEmbeddedNavigation, embedded]);

  useEffect(() => {
    if (!embedded) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== window.parent) return;
      if (event.data?.type !== "setEmbeddedDocsNavigation") return;
      setEmbeddedNavigationOpen(Boolean(event.data.open));
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [embedded]);

  useEffect(() => {
    setMobileNavigationOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!navigationOpen) return;

    const previousOverflow = document.documentElement.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeNavigation();
        return;
      }
      if (event.key !== "Tab") return;
      const panel = navigationPanelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>("a[href], button, input, select, textarea, [tabindex]"),
      ).filter(
        (element) =>
          element.tabIndex >= 0 &&
          !element.matches(":disabled, [inert] *") &&
          element.getClientRects().length > 0,
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        event.preventDefault();
        panel.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === panel)
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || document.activeElement === panel)
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.documentElement.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    const focusFrame = requestAnimationFrame(() => navigationPanelRef.current?.focus());
    return () => {
      cancelAnimationFrame(focusFrame);
      document.documentElement.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [closeNavigation, navigationOpen]);

  useEffect(() => {
    if (embedded) return;

    const desktopQuery = window.matchMedia("(min-width: 768px)");
    const closeOnDesktop = () => {
      if (desktopQuery.matches) setMobileNavigationOpen(false);
    };

    desktopQuery.addEventListener("change", closeOnDesktop);
    return () => desktopQuery.removeEventListener("change", closeOnDesktop);
  }, [embedded]);

  // If no auth session, redirect to login (this will be handled by middleware)
  return (
    <PortalProvider
      initialShellData={props.initialShellData}
      // A page in another organisation or project reloads the layout with its data (URLs carry the
      // scope), so the portal starts afresh with it.
      key={`${initialAuthState?.user.id ?? "signed-out"}:${initialSelectedOrgId ?? ""}:${initialSelectedProjectId ?? ""}`}
      defaultOrganisations={initialOrganisations}
      defaultSelectedOrgId={initialSelectedOrgId ?? null}
      defaultProjectsPage={initialProjects}
      defaultSelectedProjectId={initialSelectedProjectId}
      defaultProjectTeam={initialProjectTeam}
      defaultModerationProfiles={initialModerationProfiles}
    >
      <AuthProvider initialAuthState={initialAuthState}>
        {embedded ? <DocsLoadedNotifier /> : null}
        {navigationOpen ? (
          <div
            className={clsx(
              styles.navigation,
              embedded ? styles.embeddedNavigation : styles.mobileNavigation,
            )}
          >
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              className={styles.navigationBackdrop}
              onClick={closeNavigation}
            />
            <div
              ref={navigationPanelRef}
              aria-label={translateMessage("Navigation")}
              aria-modal="true"
              role="dialog"
              tabIndex={-1}
              data-theme={sidebarTheme}
              className={styles.navigationPanel}
              onClickCapture={(event) => {
                if ((event.target as HTMLElement).closest("a[href]")) {
                  closeNavigation();
                }
              }}
            >
              <IconButton
                label={translateMessage("Close navigation")}
                labelTooltip={false}
                variant="outline"
                className={styles.navigationCloseButton}
                onClick={closeNavigation}
              >
                <IconX size={20} aria-hidden />
              </IconButton>
              {sidebar}
            </div>
          </div>
        ) : null}
        <div
          data-product-shell="true"
          inert={navigationOpen}
          className={clsx(styles.shell, "min-h-screen bg-background")}
        >
          <a className="skipLink" href="#main-content">
            {translateMessage("Skip to main content")}
          </a>
          <SplitPane
            direction="horizontal"
            className={styles.splitPane}
            persistCookie={FIRST_PARTY_COOKIES.PORTAL_LAYOUT_SPLIT_PANE}
          >
            {!embedded ? (
              <SplitPanePanel
                id="portal-sidebar"
                data-theme={sidebarTheme}
                className={styles.sidebarPanel}
              >
                {sidebar}
              </SplitPanePanel>
            ) : null}
            <SplitPanePanel
              id="portal-content"
              className={clsx(styles.contentPanel, embedded && styles.embeddedContentPanel)}
            >
              {!embedded ? (
                <div className={styles.topbar}>
                  <PortalTopbar onOpenNavigation={() => setMobileNavigationOpen(true)} />
                </div>
              ) : null}
              <div id="main-content" tabIndex={-1} className={styles.main}>
                <StatusPageLayout>{children}</StatusPageLayout>
              </div>
              {!embedded && footer}
            </SplitPanePanel>
          </SplitPane>
        </div>
      </AuthProvider>
    </PortalProvider>
  );
};

export default AppLayout;
